import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound, ShieldCheck, ArrowLeft } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/request-reset", { email });
      setCode(data.code);
      setStep(2);
      toast.success("Reset code generated");
    } catch (err) { setError(apiError(err.response?.data?.detail) || "Something went wrong"); }
    finally { setLoading(false); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-confirm", { email, code, password });
      toast.success("Password reset! Please log in.");
      nav("/login");
    } catch (err) { setError(apiError(err.response?.data?.detail) || "Reset failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-20">
      <div className="text-center mb-8">
        <img src={BRAND.logoCircle} alt="APE-TIDES" className="h-20 w-20 object-contain mx-auto mb-4" />
        <h1 className="font-display text-4xl tracking-tight">RESET PASSWORD</h1>
        <p className="text-ape-muted text-sm mt-2">{step === 1 ? "Enter your account email to get a reset code." : "Enter your new password to get back in."}</p>
      </div>

      {step === 1 ? (
        <form onSubmit={requestCode} className="border border-ape-border bg-ape-surface p-8 space-y-6" data-testid="forgot-request-form">
          {error && <div className="border border-ape-red bg-ape-red/10 text-ape-red text-sm p-3 font-mono2" data-testid="forgot-error">{error}</div>}
          <label className="block">
            <span className="font-mono2 text-[10px] tracking-widest text-ape-muted">ACCOUNT EMAIL</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email"
              className="w-full bg-transparent border-b-2 border-ape-border rounded-none py-2 text-white focus:border-ape-red focus:outline-none transition-colors duration-150" />
          </label>
          <button disabled={loading} className="w-full bg-ape-red hover:bg-ape-redhover disabled:opacity-50 text-white font-head uppercase tracking-widest py-4 transition-colors duration-150" data-testid="forgot-request-submit">
            {loading ? "Generating…" : "Get Reset Code"}
          </button>
        </form>
      ) : (
        <form onSubmit={resetPassword} className="border border-ape-border bg-ape-surface p-8 space-y-6" data-testid="forgot-reset-form">
          {error && <div className="border border-ape-red bg-ape-red/10 text-ape-red text-sm p-3 font-mono2" data-testid="reset-error">{error}</div>}
          <div className="border border-ape-red bg-ape-red/10 p-4 text-center" data-testid="reset-code-box">
            <div className="flex items-center justify-center gap-2 font-mono2 text-[10px] tracking-widest text-ape-muted mb-2"><KeyRound size={14} className="text-ape-red" /> YOUR TEMPORARY CODE (VALID 15 MIN)</div>
            <div className="font-display text-4xl tracking-[0.3em] text-ape-red" data-testid="reset-code-value">{code}</div>
          </div>
          <label className="block">
            <span className="font-mono2 text-[10px] tracking-widest text-ape-muted">CONFIRM CODE</span>
            <input required value={code} onChange={(e) => setCode(e.target.value)} data-testid="reset-code-input"
              className="w-full bg-transparent border-b-2 border-ape-border rounded-none py-2 text-white font-mono2 focus:border-ape-red focus:outline-none transition-colors duration-150" />
          </label>
          <label className="block">
            <span className="font-mono2 text-[10px] tracking-widest text-ape-muted">NEW PASSWORD</span>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password"
              className="w-full bg-transparent border-b-2 border-ape-border rounded-none py-2 text-white focus:border-ape-red focus:outline-none transition-colors duration-150" />
          </label>
          <label className="block">
            <span className="font-mono2 text-[10px] tracking-widest text-ape-muted">CONFIRM NEW PASSWORD</span>
            <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm"
              className="w-full bg-transparent border-b-2 border-ape-border rounded-none py-2 text-white focus:border-ape-red focus:outline-none transition-colors duration-150" />
          </label>
          <button disabled={loading} className="w-full bg-ape-red hover:bg-ape-redhover disabled:opacity-50 text-white font-head uppercase tracking-widest py-4 flex items-center justify-center gap-2 transition-colors duration-150" data-testid="reset-submit">
            <ShieldCheck size={16} /> {loading ? "Resetting…" : "Set New Password"}
          </button>
        </form>
      )}

      <Link to="/login" className="flex items-center justify-center gap-2 text-ape-muted hover:text-ape-red text-sm mt-6 transition-colors duration-150" data-testid="back-to-login">
        <ArrowLeft size={14} /> Back to Login
      </Link>
    </div>
  );
}
